import React, { ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Extended Organization type with subscription fields
interface ExtendedOrganization {
  id: string;
  name: string;
  subscription_status?: string;
  subscription_plan?: string;
  [key: string]: any;
}

interface FeatureGateProps {
  /** The feature identifier to check access for */
  feature: string;
  /** Content to render when user has access */
  children: ReactNode;
  /** Optional content to show when access is denied */
  fallback?: ReactNode;
}

/**
 * A component that conditionally renders content based on subscription access
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback,
}) => {
  const { organization } = useOrganization();
  const navigate = useNavigate();
  
  // Cast organization to extended type
  const extendedOrg = organization as ExtendedOrganization | null;
  
  // Client-side feature check based on subscription status
  const hasAccess = useMemo(() => {
    
    if (!extendedOrg) return false;
    
    // Trial users have access to all features
    if (extendedOrg.subscription_status === 'trialing') return true;
    
    // Check feature access based on basic rules
    const { subscription_status, subscription_plan } = extendedOrg;
    
    if (!['active', 'paid_active', 'active-hiveshare'].includes(subscription_status || '')) return false;
    
    // Free tier has basic features
    if (!subscription_plan) {
      return ['basic_dashboard', 'student_management'].includes(feature);
    }
    
    // Basic plan features
    if (subscription_plan === 'basic') {
      return ['basic_dashboard', 'student_management', 'class_management', 'basic_reports'].includes(feature);
    }
    
    // Premium plan has access to everything except enterprise features
    if (subscription_plan === 'premium') {
      return !['white_label', 'api_access', 'custom_domain'].includes(feature);
    }
    
    // Enterprise has access to everything
    if (subscription_plan === 'enterprise') {
      return true;
    }
    
    return false;
  }, [extendedOrg, feature]);
  
  // Default fallback to show when access is denied
  const defaultFallback = (
    <div className="border border-solid border-blue-200 rounded-md p-6 flex flex-col items-center justify-center text-center space-y-4 bg-blue-50">
      <div className="rounded-full bg-blue-100 p-3">
        <Crown className="h-6 w-6 text-blue-500" />
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900">Unlock {feature}</h3>
        <p className="text-sm text-gray-500 mt-1">
          Available with Pro plan
        </p>
      </div>
      <Button
        onClick={() => navigate('/admin/settings/subscription-center')}
        variant="default"
        className="mt-4"
      >
        Upgrade to Advanced
      </Button>
    </div>
  );
  
  return hasAccess ? <>{children}</> : <>{fallback || defaultFallback}</>;
};

/**
 * Hook to check if a feature is accessible
 */
export const useFeatureAccess = (feature: string) => {
  const { organization, loading } = useOrganization();
  const extendedOrg = organization as ExtendedOrganization | null;
  
  const hasAccess = useMemo(() => {
    if (!extendedOrg) return false;
    
    // Trial users have access to all features
    if (extendedOrg.subscription_status === 'trialing') return true;
    
    // Basic client-side check
    if (!['active', 'paid_active', 'active-hiveshare'].includes(extendedOrg.subscription_status || '')) return false;
    
    // Check feature access based on plan
    const { subscription_plan } = extendedOrg;
    
    // Free tier has basic features
    if (!subscription_plan) {
      return ['basic_dashboard', 'student_management'].includes(feature);
    }
    
    // Basic plan features
    if (subscription_plan === 'basic') {
      return ['basic_dashboard', 'student_management', 'class_management', 'basic_reports'].includes(feature);
    }
    
    // Premium plan has access to everything except enterprise features
    if (subscription_plan === 'premium') {
      return !['white_label', 'api_access', 'custom_domain'].includes(feature);
    }
    
    // Enterprise has access to everything
    if (subscription_plan === 'enterprise') {
      return true;
    }
    
    return false;
  }, [extendedOrg, feature]);
  
  return { hasAccess, loading };
}; 